import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import {
  CanvasTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  SpriteMaterial,
} from "three";
import type { Vector3 } from "../simulation/types";

const FADE_IN_SECONDS = 0.35;
// World-space height of every label; width follows the measured text
const LABEL_WORLD_HEIGHT = 0.5;
const FONT_PX = 64;
const PAD_X = 40;
const PAD_Y = 22;

interface LabelSpriteProps {
  readonly text: string;
  readonly position: Vector3;
  readonly scale?: readonly [width: number, height: number, depth: number];
  readonly color?: string;
}

interface LabelResources {
  readonly material: SpriteMaterial;
  readonly worldScale: readonly [number, number, number];
}

function createLabel(text: string, color: string): LabelResources {
  const display = text.toUpperCase();
  const font = `600 ${FONT_PX}px "Space Grotesk", Inter, system-ui, sans-serif`;
  const probe = document.createElement("canvas").getContext("2d");

  if (probe === null) {
    throw new Error("Unable to create the label texture");
  }

  probe.font = font;
  if ("letterSpacing" in probe) {
    probe.letterSpacing = "6px";
  }
  const textWidth = Math.ceil(probe.measureText(display).width);
  const width = textWidth + PAD_X * 2;
  const height = FONT_PX + PAD_Y * 2;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  if (context === null) {
    throw new Error("Unable to create the label texture");
  }

  context.font = font;
  if ("letterSpacing" in context) {
    context.letterSpacing = "6px";
  }
  context.textAlign = "center";
  context.textBaseline = "middle";

  // Tight dark outline keeps text legible over stars without softening it
  context.lineJoin = "round";
  context.strokeStyle = "rgba(2, 8, 18, 0.9)";
  context.lineWidth = 8;
  context.strokeText(display, width / 2, height / 2 + 2);
  context.fillStyle = color;
  context.fillText(display, width / 2, height / 2 + 2);

  const texture = new CanvasTexture(canvas);
  // Mipmapped trilinear + anisotropic filtering keeps minified text crisp
  texture.minFilter = LinearMipmapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 16;

  const material = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    opacity: 0,
  });

  // Preserve the texture's aspect so text is never stretched
  const worldWidth = LABEL_WORLD_HEIGHT * (width / height);

  return {
    material,
    worldScale: [worldWidth, LABEL_WORLD_HEIGHT, 1],
  };
}

export function LabelSprite({ text, position, color = "#f5f8ff" }: LabelSpriteProps) {
  const { material, worldScale } = useMemo(
    () => createLabel(text, color),
    [color, text],
  );

  useFrame((_, delta) => {
    if (material.opacity < 1) {
      material.opacity = Math.min(
        1,
        material.opacity + delta / FADE_IN_SECONDS,
      );
    }
  });

  useEffect(
    () => () => {
      material.map?.dispose();
      material.dispose();
    },
    [material],
  );

  return <sprite material={material} position={position} scale={worldScale} />;
}
